import {strictLocalDateTimeToIso} from '../journal/schedule.js';
import {plannerTimezoneReview} from './timezone.js';
import {plannerSourceFromSessionBundle} from './source-adapter.js';
import {generateBackwardSchedule} from './backward-scheduler.js';
import {
  plannerCompatibilityRequirementsFromSessionBundle,
  validatePlannerCompatibility
} from './compatibility.js';
import {
  acknowledgePlannerFinding,
  createPlannerReview,
  overridePlannerBlock,
  plannerReviewApplication,
  resetPlannerReview
} from './review.js';
import {renderPlannerPage} from './page-view.js';

const ELIGIBLE_STATUSES=new Set(['draft','planned','preparation','ready']);

function text(value){return String(value??'').trim();}

function integer(value,{min=0,max=1440,label='value'}={}){
  const number=Number(value);
  if(!Number.isInteger(number)||number<min||number>max){
    throw new TypeError(label+' must be an integer between '+min+' and '+max);
  }
  return number;
}

function boolean(value){
  return value===true||value==='true'||value==='1'||value==='on';
}

function defaultTimeZone(){
  try{return Intl.DateTimeFormat().resolvedOptions().timeZone||'UTC';}
  catch{return 'UTC';}
}

function sessionLabel(session){
  return text(session?.title)||'Wędzenie bez nazwy';
}

function eligibleSessions(rows=[]){
  return Object.freeze(
    (rows||[])
      .filter(session=>ELIGIBLE_STATUSES.has(String(session?.status||'')))
      .map(session=>Object.freeze({
        id:String(session.id),
        title:sessionLabel(session),
        status:String(session.status),
        recipeDerived:Boolean(session.recipeId),
        smokerId:session.smokerId||null,
        plannedStartAt:session.plannedStartAt||null,
        plannedEndAt:session.plannedEndAt||null
      }))
  );
}

function productCategories(bundle,refs){
  const catalogue=new Map((refs?.productCatalogue||[]).map(item=>[String(item.code),item.categoryKey]));
  const custom=new Map((refs?.customProducts||[]).map(item=>[String(item.id),item.categoryKey]));
  const result=[];
  for(const item of bundle?.items||[]){
    const category=item.catalogueProductCode
      ?catalogue.get(String(item.catalogueProductCode))
      :item.customProductId
        ?custom.get(String(item.customProductId))
        :null;
    if(category&&!result.includes(category))result.push(category);
  }
  return Object.freeze(result);
}

function defaultSmokerId(bundle,refs){
  if(bundle?.session?.smokerId)return String(bundle.session.smokerId);
  const smokers=refs?.smokers||[];
  return smokers.find(item=>item.isDefault)?.id||smokers[0]?.id||null;
}

function capabilitiesFor(smokerId,refs){
  const id=text(smokerId);
  if(!id)return null;
  return (refs?.capabilities||[]).find(item=>
    String(item.smokerId||item.id||'')===id
  )||{smokerId:id};
}

function localOverride(value,timeZone,label){
  const raw=text(value);
  return raw?strictLocalDateTimeToIso(raw,{timeZone}):null;
}

export function createPlannerPageController({
  journalRuntime,
  woodGuidance=[],
  reminderScheduler=null,
  plannerAnalytics=null,
  timeZone=defaultTimeZone,
  now=()=>new Date().toISOString(),
  isOnline=()=>globalThis.navigator?.onLine!==false,
  runIdFactory=()=>globalThis.crypto?.randomUUID?.()
    ||('planner_'+Date.now().toString(36)+'_'+Math.random().toString(36).slice(2,10))
}={}){
  if(!journalRuntime?.sessions?.list||!journalRuntime?.sessions?.update||!journalRuntime?.sessionBundle){
    throw new TypeError('Journal runtime is required');
  }
  if(typeof journalRuntime.referenceData!=='function'){
    throw new TypeError('Journal referenceData is required');
  }
  if(!Array.isArray(woodGuidance))throw new TypeError('woodGuidance must be an array');

  let selectedBundle=null;
  let refs=null;
  let review=null;
  let generatedInput=null;
  let openedTracked=false;
  let state=Object.freeze({
    kind:'loading',
    sessions:Object.freeze([]),
    notice:'',
    noticeKind:'neutral'
  });

  async function listState({notice='',noticeKind='neutral'}={}){
    const sessions=eligibleSessions(await journalRuntime.sessions.list());
    state=Object.freeze({
      kind:'list',
      sessions,
      notice,
      noticeKind
    });
    return state;
  }

  async function refresh(){
    state=Object.freeze({...state,kind:'loading'});
    try{
      const next=await listState();
      if(!openedTracked){
        plannerAnalytics?.opened?.({
          eligibleSessionCount:next.sessions.length,
          offline:!isOnline()
        });
        openedTracked=true;
      }
      return next;
    }
    catch{
      state=Object.freeze({kind:'error',sessions:Object.freeze([])});
      return state;
    }
  }

  async function selectSession(sessionId){
    const id=text(sessionId);
    if(!id)throw new TypeError('sessionId is required');
    const [bundle,referenceData,reminderPreferences]=await Promise.all([
      journalRuntime.sessionBundle(id),
      journalRuntime.referenceData(),
      reminderScheduler?.getPreferences?.()||Promise.resolve({enabled:false})
    ]);
    if(!bundle)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(!ELIGIBLE_STATUSES.has(String(bundle.session.status||''))){
      throw new Error('PLANNER_SESSION_NOT_EDITABLE');
    }
    selectedBundle=bundle;
    refs=referenceData;
    review=null;
    generatedInput=null;
    const zone=text(timeZone())||'UTC';
    const timezoneReview=bundle.session.plannerScheduleSnapshot
      ?plannerTimezoneReview(bundle.session.plannerScheduleSnapshot,zone)
      :null;
    state=Object.freeze({
      kind:'setup',
      session:Object.freeze({
        id:bundle.session.id,
        title:sessionLabel(bundle.session),
        status:bundle.session.status,
        recipeDerived:Boolean(bundle.session.recipeId)
      }),
      smokers:Object.freeze([...(refs.smokers||[])]),
      selectedSmokerId:defaultSmokerId(bundle,refs),
      timeZone:zone,
      timezoneReview,
      bufferMinutes:30,
      coldSmokingRequired:false,
      remindersEnabled:Boolean(reminderPreferences?.enabled),
      notice:'',
      noticeKind:'neutral'
    });
    return state;
  }

  async function generate({
    targetReadyLocal,
    timeZone:inputTimeZone=state.timeZone,
    bufferMinutes=state.bufferMinutes??30,
    smokerId=state.selectedSmokerId,
    coldSmokingRequired=false,
    remindersEnabled=state.remindersEnabled??false
  }={}){
    if(!selectedBundle||state.kind!=='setup'&&state.kind!=='review'&&state.kind!=='applied'){
      throw new Error('PLANNER_SESSION_REQUIRED');
    }
    const zone=text(inputTimeZone)||'UTC';
    const targetReadyAt=strictLocalDateTimeToIso(targetReadyLocal,{timeZone:zone});
    if(!targetReadyAt)throw new TypeError('targetReadyLocal is required');
    const buffer=integer(bufferMinutes,{min:0,max:1440,label:'bufferMinutes'});
    const previousBuffer=generatedInput?.bufferMinutes??state.bufferMinutes??30;
    const plannerRunId=text(runIdFactory());
    if(!plannerRunId)throw new Error('PLANNER_RUN_ID_UNAVAILABLE');
    plannerAnalytics?.targetTimeSet?.({
      plannerRunId,
      bufferMinutes:buffer,
      remindersEnabled:boolean(remindersEnabled),
      recipeDerived:Boolean(selectedBundle.session.recipeId),
      offline:!isOnline()
    });
    if(previousBuffer!==buffer){
      plannerAnalytics?.bufferChanged?.({
        plannerRunId,
        previousMinutes:previousBuffer,
        nextMinutes:buffer,
        offline:!isOnline()
      });
    }
    const source=plannerSourceFromSessionBundle(selectedBundle);
    const schedule=generateBackwardSchedule({
      source,
      targetReadyAt,
      timeZone:zone,
      bufferMinutes:buffer
    });
    const categories=productCategories(selectedBundle,refs);
    const requirements=plannerCompatibilityRequirementsFromSessionBundle(selectedBundle,{
      coldSmokingRequired:boolean(coldSmokingRequired),
      productCategories:categories
    });
    const compatibility=validatePlannerCompatibility({
      requirements,
      smokerCapabilities:capabilitiesFor(smokerId,refs),
      woodInventory:refs?.woodInventory??null,
      woodGuidance
    });
    review=createPlannerReview(schedule,{compatibility});
    const warningFindings=compatibility.findings.filter(item=>
      ['incompatible','unknown','review'].includes(String(item.status||''))
    );
    const localDates=new Set(
      schedule.blocks
        .flatMap(block=>[block.startAt,block.endAt])
        .filter(Boolean)
        .map(value=>new Intl.DateTimeFormat('en-CA',{
          timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit'
        }).format(new Date(value)))
    );
    plannerAnalytics?.planGenerated?.({
      plannerRunId,
      blockCount:schedule.blocks.length,
      warningCount:schedule.warnings?.length||0,
      compatibility:compatibility.status,
      complete:schedule.complete,
      crossDay:localDates.size>1,
      remindersEnabled:boolean(remindersEnabled),
      offline:!isOnline()
    });
    if(warningFindings.length){
      plannerAnalytics?.compatibilityWarning?.({
        plannerRunId,
        compatibility:compatibility.status,
        findingCount:warningFindings.length,
        offline:!isOnline()
      });
    }
    generatedInput=Object.freeze({
      plannerRunId,
      targetReadyLocal:text(targetReadyLocal),
      targetReadyAt,
      timeZone:zone,
      bufferMinutes:buffer,
      smokerId:text(smokerId)||null,
      coldSmokingRequired:boolean(coldSmokingRequired),
      remindersEnabled:boolean(remindersEnabled),
      productCategories:categories
    });
    state=Object.freeze({
      kind:'review',
      session:state.session,
      smokers:Object.freeze([...(refs?.smokers||[])]),
      selectedSmokerId:generatedInput.smokerId,
      input:generatedInput,
      review,
      notice:'',
      noticeKind:'neutral'
    });
    return state;
  }

  function overrideBlock(blockId,{
    startLocal,
    endLocal,
    durationMinutes
  }={}){
    if(state.kind!=='review'||!review)throw new Error('PLANNER_REVIEW_REQUIRED');
    const changes={};
    if(text(startLocal))changes.startAt=localOverride(startLocal,generatedInput.timeZone,'startAt');
    if(text(endLocal))changes.endAt=localOverride(endLocal,generatedInput.timeZone,'endAt');
    if(durationMinutes!=null&&durationMinutes!=='')changes.durationMinutes=integer(durationMinutes,{
      min:1,max:365*24*60,label:'durationMinutes'
    });
    review=overridePlannerBlock(review,blockId,changes);
    const changed=review.blocks.find(block=>block.id===String(blockId));
    plannerAnalytics?.manualOverride?.({
      plannerRunId:generatedInput?.plannerRunId,
      blockKind:changed?.kind,
      changedFieldCount:changed?.manualFields?.length||0,
      downstreamCount:review.lastImpact?.affectedBlockIds?.length||0,
      impact:review.lastImpact?.boundary||'unknown',
      offline:!isOnline()
    });
    state=Object.freeze({...state,review,notice:'Zapisano ręczną zmianę. Sprawdź wpływ na dalszy harmonogram.',noticeKind:'warning'});
    return state;
  }

  function resetGenerated(){
    if(!review)throw new Error('PLANNER_REVIEW_REQUIRED');
    review=resetPlannerReview(review);
    state=Object.freeze({...state,review,notice:'Przywrócono harmonogram wygenerowany przez Planner.',noticeKind:'neutral'});
    return state;
  }

  function acknowledgeFinding(code,acknowledged=true){
    if(!review)throw new Error('PLANNER_REVIEW_REQUIRED');
    review=acknowledgePlannerFinding(review,code,acknowledged);
    state=Object.freeze({...state,review});
    return state;
  }

  async function apply(){
    if(state.kind!=='review'||!review||!selectedBundle)throw new Error('PLANNER_REVIEW_REQUIRED');
    const application=plannerReviewApplication(review,{
      now,
      plannerRunId:generatedInput?.plannerRunId
    });
    const saved=await journalRuntime.sessions.update(selectedBundle.session.id,application);
    plannerAnalytics?.planApplied?.({
      plannerRunId:generatedInput?.plannerRunId,
      blockCount:review.blocks.length,
      manualOverrideCount:review.blocks.filter(block=>block.origin==='manual').length,
      remindersEnabled:Boolean(generatedInput?.remindersEnabled),
      compatibility:review.compatibility.status,
      offline:!isOnline()
    });
    selectedBundle=Object.freeze({...selectedBundle,session:saved});
    let reminderResult=null;
    if(reminderScheduler?.configure){
      try{
        reminderResult=await reminderScheduler.configure({
          enabled:Boolean(generatedInput?.remindersEnabled)
        });
        if(generatedInput?.remindersEnabled
          &&['enabled','configured','reconciled'].includes(String(reminderResult?.status||''))){
          plannerAnalytics?.notificationEnabled?.({
            plannerRunId:generatedInput?.plannerRunId,
            reminderCount:reminderResult?.desiredCount||0,
            offline:!isOnline()
          });
        }
      }catch{
        reminderResult=Object.freeze({status:'failed'});
      }
    }
    const reminderWarning=generatedInput?.remindersEnabled
      &&reminderResult
      &&!['enabled','configured','reconciled'].includes(String(reminderResult.status||''));
    state=Object.freeze({
      ...state,
      kind:'applied',
      applied:Object.freeze({
        plannedStartAt:saved.plannedStartAt,
        plannedEndAt:saved.plannedEndAt,
        plannerScheduleSnapshot:saved.plannerScheduleSnapshot
      }),
      reminderResult,
      notice:reminderWarning
        ?'Harmonogram został zapisany, ale lokalne przypomnienia nie są aktywne. Sprawdź uprawnienia powiadomień.'
        :'Harmonogram został zapisany w wędzeniu. Możesz wrócić do Dziennika i dalej go edytować.',
      noticeKind:reminderWarning?'warning':'success'
    });
    return saved;
  }

  async function cancel(){
    selectedBundle=null;
    refs=null;
    review=null;
    generatedInput=null;
    return listState({notice:'Planowanie anulowane. Dane wędzenia nie zostały zmienione.',noticeKind:'neutral'});
  }

  function getState(){return state;}
  function render(){return renderPlannerPage(state);}

  return Object.freeze({
    refresh,selectSession,generate,overrideBlock,resetGenerated,
    acknowledgeFinding,apply,cancel,getState,render
  });
}
